/**
 * Modern Bold · <mb-product-gallery> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * PDP image gallery: prev/next arrows, thumbnail strip, mobile scroll-snap
 * with dot pager, and a fullscreen lightbox dialog. Self-contained — no
 * dependencies on other scripts.
 *
 * Lifecycle:
 *   connectedCallback    — caches DOM refs, wires slider + lightbox
 *   disconnectedCallback — removes every listener and restores the
 *                          lightbox to its pre-portal DOM position so
 *                          a re-mount (shopify:section:load) doesn't
 *                          double-portal it.
 *
 * DOM contract (rendered by snippets/modern-product-gallery.liquid):
 *   <mb-product-gallery>
 *     [data-mb-gallery-stage]            slider stage
 *       [data-index] × N                 individual slides
 *       [data-mb-gallery-zoom]           "click to zoom" trigger (optional)
 *       [data-mb-gallery-prev|next]      arrows (optional, ≥2 images)
 *     [data-mb-gallery-thumb] × N        thumbnails (optional)
 *     [data-mb-gallery-dot] × N          mobile dots (optional)
 *     [data-mb-gallery-lightbox]         dialog (optional)
 *       [data-mb-lightbox-stage]
 *         [data-lightbox-index] × N
 *         [data-mb-lightbox-prev|next]
 *       [data-mb-gallery-lightbox-close]
 *       [data-mb-gallery-lightbox-overlay]
 *       [data-mb-lightbox-current]       counter "n"
 */
if (!customElements.get('mb-product-gallery')) {
  class MBProductGallery extends HTMLElement {
    #cleanups = [];
    #stage = null;
    #slides = [];
    #thumbBtns = [];
    #dots = [];
    #prevBtn = null;
    #nextBtn = null;
    #lightbox = null;
    #current = 0;

    connectedCallback() {
      this.#stage     = this.querySelector('[data-mb-gallery-stage]');
      this.#slides    = Array.from(this.querySelectorAll('[data-mb-gallery-stage] [data-index]'));
      this.#thumbBtns = Array.from(this.querySelectorAll('[data-mb-gallery-thumb]'));
      this.#dots      = Array.from(this.querySelectorAll('[data-mb-gallery-dot]'));
      this.#prevBtn   = this.querySelector('[data-mb-gallery-prev]');
      this.#nextBtn   = this.querySelector('[data-mb-gallery-next]');
      this.#lightbox  = this.querySelector('[data-mb-gallery-lightbox]');

      if (!this.#slides.length) return;

      this.#wireSlider();
      if (this.#lightbox) this.#wireLightbox();
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    // ── Slider ─────────────────────────────────────────────────────────────

    #isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    #goTo(idx) {
      const n = ((idx % this.#slides.length) + this.#slides.length) % this.#slides.length;

      this.#slides.forEach((s, i) => {
        const active = i === n;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', String(!active));
      });
      this.#thumbBtns.forEach((btn, i) => {
        btn.classList.toggle('is-active', i === n);
        btn.setAttribute('aria-pressed', String(i === n));
      });
      this.#dots.forEach((dot, i) => dot.classList.toggle('is-active', i === n));

      // Mobile only — desktop stage is a CSS grid (no scroll). Calling
      // scrollTo on it would fire a phantom scroll event whose listener
      // resets `current` back to 0 (the original "next reverts" bug).
      if (this.#stage && this.#stage.scrollTo && this.#isMobile()) {
        this.#stage.scrollTo({ left: this.#stage.offsetWidth * n, behavior: 'smooth' });
      }

      if (this.#prevBtn) this.#prevBtn.setAttribute('aria-label', `Previous image (${n === 0 ? this.#slides.length : n} of ${this.#slides.length})`);
      if (this.#nextBtn) this.#nextBtn.setAttribute('aria-label', `Next image (${((n + 1) % this.#slides.length) + 1} of ${this.#slides.length})`);

      this.#current = n;
    }

    #wireSlider() {
      const onPrev    = () => this.#goTo(this.#current - 1);
      const onNext    = () => this.#goTo(this.#current + 1);
      const onKeydown = (e) => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this.#goTo(this.#current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.#goTo(this.#current + 1); }
        if (e.key === 'Home')       { e.preventDefault(); this.#goTo(0); }
        if (e.key === 'End')        { e.preventDefault(); this.#goTo(this.#slides.length - 1); }
      };

      if (this.#prevBtn) this.#prevBtn.addEventListener('click', onPrev);
      if (this.#nextBtn) this.#nextBtn.addEventListener('click', onNext);
      this.addEventListener('keydown', onKeydown);

      const thumbHandlers = this.#thumbBtns.map((btn) => {
        const h = () => this.#goTo(parseInt(btn.dataset.index, 10));
        btn.addEventListener('click', h);
        return [btn, h];
      });

      this.#cleanups.push(() => {
        if (this.#prevBtn) this.#prevBtn.removeEventListener('click', onPrev);
        if (this.#nextBtn) this.#nextBtn.removeEventListener('click', onNext);
        this.removeEventListener('keydown', onKeydown);
        thumbHandlers.forEach(([btn, h]) => btn.removeEventListener('click', h));
      });

      // Mobile scroll-snap → dot/thumb sync. Bound only on mobile so the
      // desktop grid (which doesn't scroll) doesn't trigger phantom resets.
      if (this.#stage && this.#isMobile()) {
        let scrollTimer = 0;
        const onScroll = () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const idx = Math.round(this.#stage.scrollLeft / this.#stage.offsetWidth);
            if (idx === this.#current) return;
            this.#slides.forEach((s, i) => {
              s.classList.toggle('is-active', i === idx);
              s.setAttribute('aria-hidden', String(i !== idx));
            });
            this.#thumbBtns.forEach((btn, i) => {
              btn.classList.toggle('is-active', i === idx);
              btn.setAttribute('aria-pressed', String(i === idx));
            });
            this.#dots.forEach((dot, i) => dot.classList.toggle('is-active', i === idx));
            this.#current = idx;
          }, 80);
        };
        this.#stage.addEventListener('scroll', onScroll, { passive: true });
        this.#cleanups.push(() => {
          clearTimeout(scrollTimer);
          this.#stage.removeEventListener('scroll', onScroll);
        });
      }
    }

    // ── Lightbox ───────────────────────────────────────────────────────────

    #wireLightbox() {
      const lb = this.#lightbox;

      // Portal to <body> so the dialog escapes any sticky / transformed
      // ancestor that would otherwise create a stacking context above it.
      // Track the original parent so we can restore on disconnect.
      const originalParent = lb.parentElement;
      const originalNextSibling = lb.nextSibling;
      if (originalParent !== document.body) {
        document.body.appendChild(lb);
      }

      const lbSlides   = Array.from(lb.querySelectorAll('[data-lightbox-index]'));
      const lbPrev     = lb.querySelector('[data-mb-lightbox-prev]');
      const lbNext     = lb.querySelector('[data-mb-lightbox-next]');
      const lbClose    = lb.querySelector('[data-mb-gallery-lightbox-close]');
      const lbOverlay  = lb.querySelector('[data-mb-gallery-lightbox-overlay]');
      const lbCounter  = lb.querySelector('[data-mb-lightbox-current]');
      const zoomBtn    = this.querySelector('[data-mb-gallery-zoom]');
      let lbCurrent = 0;
      let lastTrigger = null;

      // Defer the 2400px lightbox images so they don't preload on page
      // view. Even with loading="lazy" the browser will fetch them once
      // the dialog gets portaled to <body> below, because the lazy
      // heuristic treats elements in document.body as eligible for the
      // initial scan. We sidestep that by stripping src/srcset on init
      // and restoring them per-slide the first time the modal activates
      // that slide (see ensureLoadingState).
      lbSlides.forEach((s) => {
        const img = s.querySelector('.mb-pdp-lightbox__img');
        if (!img) return;
        if (img.getAttribute('src')) {
          img.dataset.src = img.getAttribute('src');
          img.removeAttribute('src');
        }
        if (img.getAttribute('srcset')) {
          img.dataset.srcset = img.getAttribute('srcset');
          img.removeAttribute('srcset');
        }
      });
      // Third-party fixed-position widgets we squashed on open so they
      // don't paint over the dialog. Restored on close. Behavioural
      // detection (position:fixed + z-index >= 1000) catches Freshdesk,
      // a11y menus, cookie banners, etc. without an allow-list.
      let hiddenFloatingWidgets = [];

      // Track which slide images we've already wired a load listener to,
      // so re-navigating doesn't stack duplicate listeners.
      const wiredImages = new WeakSet();

      // Reset zoom + pan state on a single slide. Delegates to the
      // per-slide reset that wireZoomPan attached (which also clears the
      // closure-local scale/pan variables) when available, falling back
      // to a CSS-only reset for the brief window between init order.
      const resetZoom = (slide) => {
        if (!slide) return;
        if (typeof slide.__mbResetZoom === 'function') {
          slide.__mbResetZoom();
          return;
        }
        slide.classList.remove('is-zoomed', 'is-panning');
        const img = slide.querySelector('.mb-pdp-lightbox__img');
        if (img) {
          img.style.removeProperty('--mb-zoom-scale');
          img.style.removeProperty('--mb-zoom-tx');
          img.style.removeProperty('--mb-zoom-ty');
        }
      };

      const ensureLoadingState = (slide) => {
        if (!slide) return;
        const img = slide.querySelector('.mb-pdp-lightbox__img');
        if (!img) return;
        // First activation: restore the real src from data-src so the
        // browser only kicks off the 2400px fetch now, not on page load.
        if (!img.getAttribute('src') && img.dataset.src) {
          // Force eager loading — the original markup has loading="lazy"
          // and the slide may still be in a `hidden` ancestor or scrolled
          // off-screen in the scroll-snap stage. Some browsers defer
          // lazy loads in those states even when src is set programmatically.
          // Eager guarantees the fetch starts now.
          img.loading = 'eager';
          if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
          img.setAttribute('src', img.dataset.src);
        }
        const isReady = img.complete && img.naturalWidth > 0;
        if (isReady) {
          slide.classList.remove('is-loading');
          return;
        }
        slide.classList.add('is-loading');
        if (wiredImages.has(img)) return;
        wiredImages.add(img);
        const clear = () => slide.classList.remove('is-loading');
        img.addEventListener('load', clear, { once: true });
        img.addEventListener('error', clear, { once: true });
      };

      const lbGoTo = (idx) => {
        const n = ((idx % lbSlides.length) + lbSlides.length) % lbSlides.length;
        lbSlides.forEach((s, i) => {
          const active = i === n;
          s.classList.toggle('is-active', active);
          s.setAttribute('aria-hidden', String(!active));
          // Reset zoom on any slide we're moving away from — leaving an
          // off-screen slide zoomed would scale it the next time it
          // becomes active without an explicit user action.
          if (!active) resetZoom(s);
        });
        ensureLoadingState(lbSlides[n]);
        if (lbCounter) lbCounter.textContent = String(n + 1);
        lbCurrent = n;
      };

      const open = (idx, trigger) => {
        lastTrigger = trigger || document.activeElement;
        lbGoTo(typeof idx === 'number' ? idx : this.#current);
        lb.hidden = false;
        // Force reflow so the entry transition runs
        // eslint-disable-next-line no-unused-expressions
        lb.offsetHeight;
        lb.classList.add('is-open');

        // iOS-safe scroll lock. `overflow: hidden` on body doesn't stop
        // touch scroll on mobile Safari — the page underneath continues
        // to scroll behind the modal. The position:fixed + top:-scrollY
        // pattern is the standard fix: take body out of the document
        // flow at its current scroll offset, restore on close.
        const scrollY = window.scrollY || window.pageYOffset || 0;
        document.body.dataset.mbLightboxScroll = String(scrollY);
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';

        hiddenFloatingWidgets = [];
        Array.from(document.body.children).forEach((el) => {
          if (el === lb || el.contains(lb)) return;
          if (!el.hasAttribute('data-mb-was-inert')) {
            el.setAttribute('data-mb-was-inert', el.hasAttribute('inert') ? '1' : '0');
            el.setAttribute('inert', '');
          }
          const cs = getComputedStyle(el);
          const z = parseInt(cs.zIndex, 10);
          if (cs.position === 'fixed' && !Number.isNaN(z) && z >= 1000 && cs.display !== 'none') {
            hiddenFloatingWidgets.push({ el, prevDisplay: el.style.display });
            el.style.setProperty('display', 'none', 'important');
          }
        });
        requestAnimationFrame(() => lbClose && lbClose.focus());
      };

      const close = () => {
        lb.classList.remove('is-open');
        // Always exit zoomed state when closing — opening fresh next time
        // should start at scale 1, not whatever the last view was at.
        lbSlides.forEach(resetZoom);

        // Restore the iOS-safe scroll lock and scroll the user back to
        // where they were. Reading the saved offset off the body's data
        // attribute keeps this resilient if multiple opens stack up
        // (unlikely but safe).
        const savedScroll = parseInt(document.body.dataset.mbLightboxScroll || '0', 10);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        delete document.body.dataset.mbLightboxScroll;
        window.scrollTo(0, savedScroll);
        Array.from(document.body.children).forEach((el) => {
          if (el.hasAttribute('data-mb-was-inert')) {
            if (el.getAttribute('data-mb-was-inert') === '0') el.removeAttribute('inert');
            el.removeAttribute('data-mb-was-inert');
          }
        });
        hiddenFloatingWidgets.forEach(({ el, prevDisplay }) => {
          if (prevDisplay) el.style.setProperty('display', prevDisplay);
          else el.style.removeProperty('display');
        });
        hiddenFloatingWidgets = [];
        setTimeout(() => { lb.hidden = true; }, 250);
        if (lbCurrent !== this.#current) this.#goTo(lbCurrent);
        if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
      };

      // ── Zoom + pan wiring ────────────────────────────────────────────────
      // One handler set per slide image. Behaviour:
      //   • pointerdown/move/up on the image → click toggles zoom; drag
      //     pans (when already zoomed)
      //   • wheel on the image while zoomed → pan via deltaX/deltaY
      //     (trackpad two-finger scroll natively generates wheel events)
      //   • Click-vs-drag is distinguished by movement threshold (5px). A
      //     pointerup with no significant movement is treated as click.
      //   • Pan offsets clamped to the slide bounds so the image always
      //     overlaps the centre of the viewport.
      //   • setPointerCapture keeps move/up events flowing to the image
      //     even if the cursor leaves it mid-drag — no document-level
      //     listener needed.
      const MIN_SCALE = 1;
      const MAX_SCALE = 6;
      const CLICK_ZOOM = 4;       // jump-zoom level on a plain click
      const WHEEL_SENSITIVITY = 0.005;
      const MOVE_THRESHOLD = 5;
      const zoomCleanups = [];

      const wireZoomPan = (slide, img) => {
        // Progressive zoom state. scale ∈ [MIN_SCALE, MAX_SCALE]; panX/panY
        // are visual-pixel translations applied after the scale.
        let scale = 1;
        let panX = 0, panY = 0;
        let baseX = 0, baseY = 0;
        let startX = 0, startY = 0;
        let dragging = false;
        let moved = false;

        // Multi-touch state for pinch-zoom on mobile.
        //   activePointers: pointerId → { x, y } of every finger currently down
        //   pinchStartDist / pinchStartScale: baseline captured when the
        //     second finger lands, used to derive the pinch ratio
        //   gestureHadMultitouch: latched true on any 2+ pointer event so
        //     the eventual single-finger pointerup doesn't get misread as
        //     a tap and re-trigger click-to-zoom on top of the pinch
        const activePointers = new Map();
        let pinchStartDist = 0;
        let pinchStartScale = 1;
        let gestureHadMultitouch = false;

        // The image's rendered (pre-transform) dimensions — image uses
        // max-width/max-height 100% + object-fit:contain inside the slide,
        // so a portrait image in a landscape slide is letterboxed and the
        // visible image is narrower than the slide. Pan bounds must be
        // computed from this real image rect, NOT the slide rect, or we
        // let the image edge fly past the viewport edge into the void.
        const renderedImageBounds = () => {
          const sr = slide.getBoundingClientRect();
          if (!img.naturalWidth || !img.naturalHeight) {
            return { w: sr.width, h: sr.height };
          }
          const imgRatio = img.naturalWidth / img.naturalHeight;
          const slideRatio = sr.width / sr.height;
          if (imgRatio > slideRatio) {
            return { w: sr.width, h: sr.width / imgRatio };
          }
          return { w: sr.height * imgRatio, h: sr.height };
        };

        const maxOffsets = (s = scale) => {
          const b = renderedImageBounds();
          const sr = slide.getBoundingClientRect();
          // Clamp pan so the scaled image edge can travel exactly to the
          // slide edge — never past. If the scaled image is narrower than
          // the slide in some axis (extreme aspect mismatch + low scale),
          // the result would be negative; Math.max(0, …) keeps it at 0.
          return {
            x: Math.max(0, (s * b.w - sr.width) / 2),
            y: Math.max(0, (s * b.h - sr.height) / 2),
          };
        };

        // Apply a scale change with a focal point (screen coords). Cursor
        // pixel under the focal point stays put: panNew = pan·r + dx·(1−r)
        // where r = newScale/oldScale and dx is cursor offset from image
        // centre. Toggles .is-zoomed when crossing the scale=1 boundary
        // and writes the scale + pan CSS vars in a single batch.
        const setScale = (newScale, focalX, focalY) => {
          const s = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
          if (s === scale) return;
          if (s === 1) {
            // Snap back to identity — no need for focal-point math at
            // the un-zoomed state.
            scale = 1;
            panX = 0; panY = 0;
            slide.classList.remove('is-zoomed', 'is-panning');
            img.style.removeProperty('--mb-zoom-scale');
            img.style.removeProperty('--mb-zoom-tx');
            img.style.removeProperty('--mb-zoom-ty');
            return;
          }
          if (focalX != null && focalY != null) {
            // Use slide rect (NOT img rect) as the reference origin.
            // The img element is what gets transformed by scale + pan,
            // so its getBoundingClientRect() returns the CURRENT visible
            // bbox — which shifts every frame during pinch and causes
            // the zoom to oscillate. The slide doesn't transform, so its
            // centre stays put, and (thanks to the slide's flex centring
            // of the image) it coincides with the image's natural
            // pre-transform centre — exactly what the focal-point pan
            // math requires.
            const r = slide.getBoundingClientRect();
            const cx = r.left + r.width / 2;
            const cy = r.top + r.height / 2;
            const dx = focalX - cx;
            const dy = focalY - cy;
            const ratio = s / scale;
            panX = panX * ratio + dx * (1 - ratio);
            panY = panY * ratio + dy * (1 - ratio);
          }
          scale = s;
          // Re-clamp the pan in case the new scale's bound is tighter
          // than the previous pan position (e.g. zooming out).
          const m = maxOffsets();
          panX = Math.max(-m.x, Math.min(m.x, panX));
          panY = Math.max(-m.y, Math.min(m.y, panY));
          slide.classList.add('is-zoomed');
          img.style.setProperty('--mb-zoom-scale', String(scale));
          img.style.setProperty('--mb-zoom-tx', `${panX}px`);
          img.style.setProperty('--mb-zoom-ty', `${panY}px`);
        };
        const clamp = (v, m) => Math.max(-m, Math.min(m, v));
        const applyPan = (x, y) => {
          panX = x; panY = y;
          img.style.setProperty('--mb-zoom-tx', `${x}px`);
          img.style.setProperty('--mb-zoom-ty', `${y}px`);
        };

        const onDown = (e) => {
          // Multi-touch (pinch) is owned by the TouchEvent handlers below
          // — PointerEvent multi-touch is unreliable on iOS Safari when
          // touch-action: none is set on an ancestor. Bail out of the
          // single-pointer drag path if another finger is already down.
          if (e.pointerType === 'touch' && gestureHadMultitouch) return;
          if (e.button !== undefined && e.button !== 0) return;
          startX = e.clientX; startY = e.clientY;
          baseX = panX; baseY = panY;
          dragging = true; moved = false;
          if (img.setPointerCapture) {
            try { img.setPointerCapture(e.pointerId); } catch { /* ignore */ }
          }
        };

        const onMove = (e) => {
          if (gestureHadMultitouch) return;
          if (!dragging) return;
          const dx = e.clientX - startX;
          const dy = e.clientY - startY;
          if (!moved && Math.hypot(dx, dy) > MOVE_THRESHOLD) {
            moved = true;
            if (scale > 1) slide.classList.add('is-panning');
          }
          if (moved && scale > 1) {
            const m = maxOffsets();
            applyPan(clamp(baseX + dx, m.x), clamp(baseY + dy, m.y));
          }
        };

        const onUp = (e) => {
          slide.classList.remove('is-panning');
          dragging = false;
          if (img.releasePointerCapture) {
            try { img.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
          }
          // Suppress the tap-toggle when the gesture involved a pinch —
          // the TouchEvent handlers set gestureHadMultitouch and the
          // trailing pointerup (lifting the last finger) shouldn't be
          // misread as a tap that re-applies CLICK_ZOOM on top.
          if (gestureHadMultitouch) {
            // Clear the flag only when no fingers remain — otherwise a
            // brief second-finger lift in the middle of a pinch would
            // un-suppress the next pointerup mid-gesture.
            if (!activePointers.size) gestureHadMultitouch = false;
            return;
          }
          // Un-zoomed horizontal swipe → previous / next slide.
          // Threshold: 50px AND horizontal travel > 1.5× vertical so
          // a near-vertical drag (e.g. user about to pinch) doesn't
          // accidentally flip the slide. Only fires at scale === 1
          // because a drag while zoomed is pan, not nav.
          if (moved && scale === 1) {
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
              if (dx < 0) lbGoTo(lbCurrent + 1);
              else        lbGoTo(lbCurrent - 1);
            }
            return;
          }

          if (!moved) {
            // Pointerup without significant movement → treat as click.
            // Toggle between un-zoomed and the click-zoom level,
            // regardless of any intermediate wheel-zoom state. Cursor
            // position is the focal point for the zoom-in transition.
            if (scale > 1) {
              setScale(1);
            } else {
              setScale(CLICK_ZOOM, e.clientX, e.clientY);
            }
          }
        };

        // ── TouchEvent-based pinch ──────────────────────────────────────
        // iOS Safari with touch-action:none reliably fires TouchEvents
        // with the full e.touches list for multi-touch, where its
        // PointerEvent stream can drop the second pointer. We use
        // TouchEvents exclusively for pinch and let PointerEvents
        // handle the single-finger drag + click path above.
        const onTouchStart = (e) => {
          // Maintain a tiny presence-count so the pointerup handler
          // knows when the last finger has lifted.
          for (let i = 0; i < e.changedTouches.length; i++) {
            activePointers.set(e.changedTouches[i].identifier, true);
          }
          if (e.touches.length >= 2) {
            const t0 = e.touches[0];
            const t1 = e.touches[1];
            pinchStartDist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
            pinchStartScale = scale;
            gestureHadMultitouch = true;
            // Cancel any drag in flight — the gesture has become a pinch.
            dragging = false;
            slide.classList.remove('is-panning');
            // CSS class disables the .25s transform transition so each
            // touchmove tracks the fingers 1:1 instead of animating
            // through intermediate scales (which read as oscillation).
            slide.classList.add('is-pinching');
          }
        };

        const onTouchMove = (e) => {
          if (e.touches.length >= 2 && pinchStartDist > 0) {
            // preventDefault so iOS doesn't try to do anything else with
            // the gesture (it shouldn't with touch-action:none, but the
            // explicit cancel is defence in depth).
            e.preventDefault();
            const t0 = e.touches[0];
            const t1 = e.touches[1];
            const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
            const newScale = pinchStartScale * (dist / pinchStartDist);
            const fx = (t0.clientX + t1.clientX) / 2;
            const fy = (t0.clientY + t1.clientY) / 2;
            setScale(newScale, fx, fy);
          }
        };

        const onTouchEnd = (e) => {
          for (let i = 0; i < e.changedTouches.length; i++) {
            activePointers.delete(e.changedTouches[i].identifier);
          }
          if (e.touches.length < 2) {
            // Pinch broken — clear the start distance so a subsequent
            // 2-finger gesture starts fresh.
            pinchStartDist = 0;
            slide.classList.remove('is-pinching');
          }
          // Clear the multitouch latch the moment the last finger is
          // off the screen. On iOS Safari pointerup can fire BEFORE the
          // matching touchend updates activePointers, so doing this in
          // the pointerup handler reads a stale "still pinching" state
          // and the latch never clears — which then blocks the next
          // single-finger drag (onDown returns early on touch pointers
          // when gestureHadMultitouch is still true).
          if (e.touches.length === 0) {
            gestureHadMultitouch = false;
          }
        };

        // Wheel / two-finger trackpad scroll → progressive zoom around
        // the cursor. Negative deltaY (scroll up / two-finger swipe up
        // on most trackpads) zooms in; positive zooms out. The step is
        // proportional to current scale (multiplicative) so zooming in
        // feels uniform across the range. preventDefault stops the
        // wheel from also scrolling the page underneath.
        const onWheel = (e) => {
          e.preventDefault();
          const step = -e.deltaY * WHEEL_SENSITIVITY;
          const newScale = scale * (1 + step);
          setScale(newScale, e.clientX, e.clientY);
        };

        // Per-slide reset exposed to the outer resetZoom() helper.
        // Clears the closure-local scale/pan state alongside the CSS,
        // so navigating away from a wheel-zoomed slide really does
        // start fresh next time it becomes active.
        const reset = () => {
          scale = 1;
          panX = 0; panY = 0;
          slide.classList.remove('is-zoomed', 'is-panning', 'is-pinching');
          img.style.removeProperty('--mb-zoom-scale');
          img.style.removeProperty('--mb-zoom-tx');
          img.style.removeProperty('--mb-zoom-ty');
        };
        slide.__mbResetZoom = reset;
        // Multiplicative scale step exposed for keyboard zoom (+ / −).
        // No focal point → setScale defaults to centred zoom, which is
        // what a keyboard user expects (no cursor / pinch midpoint).
        slide.__mbScaleBy = (factor) => setScale(scale * factor);

        img.addEventListener('pointerdown', onDown);
        img.addEventListener('pointermove', onMove);
        img.addEventListener('pointerup', onUp);
        img.addEventListener('pointercancel', onUp);
        img.addEventListener('wheel', onWheel, { passive: false });
        // TouchEvents bound on the SLIDE (parent of img) so a pinch
        // that starts on the dark letterbox area is still captured.
        // passive:false on touchmove so we can preventDefault during
        // pinch and stop iOS from doing anything else with the gesture.
        slide.addEventListener('touchstart', onTouchStart, { passive: true });
        slide.addEventListener('touchmove', onTouchMove, { passive: false });
        slide.addEventListener('touchend', onTouchEnd);
        slide.addEventListener('touchcancel', onTouchEnd);

        return () => {
          img.removeEventListener('pointerdown', onDown);
          img.removeEventListener('pointermove', onMove);
          img.removeEventListener('pointerup', onUp);
          img.removeEventListener('pointercancel', onUp);
          img.removeEventListener('wheel', onWheel);
          slide.removeEventListener('touchstart', onTouchStart);
          slide.removeEventListener('touchmove', onTouchMove);
          slide.removeEventListener('touchend', onTouchEnd);
          slide.removeEventListener('touchcancel', onTouchEnd);
          delete slide.__mbResetZoom;
          delete slide.__mbScaleBy;
        };
      };

      lbSlides.forEach((s) => {
        const img = s.querySelector('.mb-pdp-lightbox__img');
        if (img) zoomCleanups.push(wireZoomPan(s, img));
      });

      const handlers = [];
      const bind = (el, evt, fn) => { el.addEventListener(evt, fn); handlers.push([el, evt, fn]); };

      if (this.#stage) {
        bind(this.#stage, 'click', (e) => {
          // Bail on any interactive descendant — clicks on the wishlist
          // heart (mb-wishlist-button is a custom element, not a literal
          // <button>), arrow nav, zoom hint, or any link should fire
          // their own handler, not pop the lightbox.
          if (e.target.closest('button, a, mb-wishlist-button, [role="button"]')) return;
          open(this.#current, this.#stage);
        });
      }
      if (zoomBtn) {
        bind(zoomBtn, 'click', (e) => {
          e.stopPropagation();
          open(this.#current, zoomBtn);
        });
      }
      if (lbClose)   bind(lbClose,   'click', close);
      if (lbOverlay) bind(lbOverlay, 'click', close);
      if (lbPrev)    bind(lbPrev,    'click', () => lbGoTo(lbCurrent - 1));
      if (lbNext)    bind(lbNext,    'click', () => lbGoTo(lbCurrent + 1));

      bind(lb, 'keydown', (e) => {
        if (e.key === 'Escape')     { e.preventDefault(); close(); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); lbGoTo(lbCurrent - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); lbGoTo(lbCurrent + 1); }
        if (e.key === 'Home')       { e.preventDefault(); lbGoTo(0); }
        if (e.key === 'End')        { e.preventDefault(); lbGoTo(lbSlides.length - 1); }
        // Keyboard zoom — mirrors the browser's native zoom shortcuts
        // (+ in, − out, 0 reset). Routes through the active slide's
        // exposed __mbScaleBy / __mbResetZoom helpers so the centred
        // zoom uses the same setScale + clamp pipeline as wheel/pinch.
        // `=` is the unshifted key on a US layout where + lives, so
        // both keys map to zoom-in.
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          const slide = lbSlides[lbCurrent];
          if (slide && slide.__mbScaleBy) slide.__mbScaleBy(1.25);
        }
        if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          const slide = lbSlides[lbCurrent];
          if (slide && slide.__mbScaleBy) slide.__mbScaleBy(0.8);
        }
        if (e.key === '0') {
          e.preventDefault();
          const slide = lbSlides[lbCurrent];
          if (slide && slide.__mbResetZoom) slide.__mbResetZoom();
        }
      });


      this.#cleanups.push(() => {
        handlers.forEach(([el, evt, fn]) => el.removeEventListener(evt, fn));
        zoomCleanups.forEach((fn) => fn());
        // Restore the lightbox to its original position so a re-mount
        // (theme-editor section reload) doesn't double-portal it to body.
        if (originalParent && lb.parentElement === document.body) {
          if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
            originalParent.insertBefore(lb, originalNextSibling);
          } else {
            originalParent.appendChild(lb);
          }
        }
      });
    }
  }
  customElements.define('mb-product-gallery', MBProductGallery);
}
